import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { NotificationService } from './notification.service';
import { TruthManagement } from './truth-management';

export interface ClientCapabilities {
  sequencingEnabled: boolean;
  extractionEnabled: boolean;
  clientId: string;
  clientName: string;
}

@Injectable({ providedIn: 'root' })
export class ClientCapabilityService {
  private cache = new Map<string, ClientCapabilities>();
  private capabilitiesSubject = new BehaviorSubject<ClientCapabilities | null>(null);
  public capabilities$ = this.capabilitiesSubject.asObservable();

  constructor(
    private truth: TruthManagement,
    private authService: AuthService,
    private router: Router,
    private notify: NotificationService,
  ) {
    this.authService.currentClient$.subscribe((client) => {
      if (!client) {
        this.capabilitiesSubject.next(null);
        return;
      }
      this.load(client.client_id, client.client_name).subscribe();
    });
  }

  load(clientId: string, clientName: string): Observable<ClientCapabilities> {
    const cached = this.cache.get(clientId);
    if (cached) {
      this.capabilitiesSubject.next(cached);
      return of(cached);
    }
    return this.truth.getClientMetadata(clientId).pipe(
      map((resp) => ({
        sequencingEnabled: !!(resp as { sequencing_enabled?: boolean })?.sequencing_enabled,
        extractionEnabled: !!(resp as { extraction_enabled?: boolean })?.extraction_enabled,
        clientId,
        clientName,
      } as ClientCapabilities)),
      tap((caps) => {
        this.cache.set(clientId, caps);
        this.capabilitiesSubject.next(caps);
      }),
      catchError(() => {
        const fallback: ClientCapabilities = {
          sequencingEnabled: false,
          extractionEnabled: false,
          clientId,
          clientName,
        };
        this.capabilitiesSubject.next(fallback);
        return of(fallback);
      }),
    );
  }

  private readonly OPS_ROUTE_INDEXING = '/indexing';
  private readonly OPS_ROUTE_EXTRACTION = '/operations-request-analytics';
  private readonly SHARED_DATE_FILTER_KEY = 'docsila.sharedDateFilter.operationsScope';

  /** UTC window used to peek at record counts for routing. Mirrors the pages'
   *  own default: prefer the shared session-storage window (last chosen by the
   *  user across Indexing/Extraction); otherwise fall back to last 24 hours. */
  private getOpsRoutingWindow(): { startUtc: string; endUtc: string } {
    try {
      const raw = sessionStorage.getItem(this.SHARED_DATE_FILTER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { startDate?: string; endDate?: string };
        if (parsed?.startDate && parsed?.endDate) {
          const start = new Date(parsed.startDate);
          const end = new Date(parsed.endDate);
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            return { startUtc: start.toISOString(), endUtc: end.toISOString() };
          }
        }
      }
    } catch { /* fall through to default */ }
    const now = new Date();
    const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return { startUtc: start.toISOString(), endUtc: now.toISOString() };
  }

  /** Resolve the default Ops-Health landing route based on capabilities and,
   *  when both are enabled, on which page actually has records to show. */
  resolveDefaultOpsRoute(): Observable<string> {
    const client = this.authService.getCurrentClient();
    if (!client) {
      return of('/home');
    }
    return this.load(client.client_id, client.client_name).pipe(
      switchMap((caps) => {
        if (caps.sequencingEnabled && !caps.extractionEnabled) {
          return of(this.OPS_ROUTE_INDEXING);
        }
        if (!caps.sequencingEnabled && caps.extractionEnabled) {
          return of(this.OPS_ROUTE_EXTRACTION);
        }
        if (!caps.sequencingEnabled && !caps.extractionEnabled) {
          return of(this.OPS_ROUTE_INDEXING);
        }
        // Both enabled — peek at Indexing first; if empty, peek at Extraction.
        const { startUtc, endUtc } = this.getOpsRoutingWindow();
        return this.truth.getSequencingRequestDetails({
          client_id: caps.clientId,
          start_date: startUtc,
          end_date: endUtc,
          sort_by: 'started_at',
          sort_dir: 'desc',
          offset: 0,
          limit: 1,
        }).pipe(
          switchMap((idxResp) => {
            const hasIndexing = (idxResp?.data?.requests?.length ?? 0) > 0;
            if (hasIndexing) {
              return of(this.OPS_ROUTE_INDEXING);
            }
            return this.truth.getExtractionReport(startUtc, endUtc, caps.clientId).pipe(
              map((extResp) => {
                const hasExtraction = (extResp?.by_document_model?.length ?? 0) > 0;
                return hasExtraction ? this.OPS_ROUTE_EXTRACTION : this.OPS_ROUTE_INDEXING;
              }),
              catchError(() => of(this.OPS_ROUTE_INDEXING)),
            );
          }),
          catchError(() => of(this.OPS_ROUTE_INDEXING)),
        );
      }),
    );
  }

  /** Ops-Health button entry: route to enabled page based on capabilities
   *  and, when both are enabled, on which page actually has records. */
  navigateToOpsHealth(): void {
    this.resolveDefaultOpsRoute().subscribe((route) => {
      this.router.navigate([route]);
    });
  }

  /** Redirect helper for pages that gate on a capability. */
  enforcePageCapability(
    kind: 'sequencing' | 'extraction',
    _options: { showToast?: boolean } = {},
  ): void {
    const caps = this.capabilitiesSubject.getValue();
    if (!caps) { return; }
    const currentAllowed = kind === 'sequencing' ? caps.sequencingEnabled : caps.extractionEnabled;
    if (currentAllowed) { return; }
    if (kind === 'sequencing' && caps.extractionEnabled) {
      this.router.navigate(['/operations-request-analytics']);
    } else if (kind === 'extraction' && caps.sequencingEnabled) {
      this.router.navigate(['/indexing']);
    }
    // else: neither enabled — no redirect; page renders its "Contact admin" empty state.
  }
}
